
import { Component, Vue } from 'vue-property-decorator'
import Composer from '@/components/Composer.vue'
import { Charm } from '@/models/Charm'
import Gallery from '@/components/Gallery.vue'
import { ProductDetail } from '@/models/ProductDetail'
import { BaseDetail } from '@/interfaces/BaseDetail'
import { CharmTag } from '@/interfaces/CharmTag'
import { BaseCharm } from '@/interfaces/BaseCharm'
import ProductSelector from '@/components/ProductSelector.vue'
import { HotSpot } from '@/interfaces/HotSpot'
import CharmsFilters from '@/components/CharmsFilters.vue'
import Summary from '@/components/Summary.vue'
import { PdfBuilder } from '@/services/PdfBuilder'
import strings from '@/assets/strings.json'
import { MutationTypes } from '@/store/mutation-types'

declare global {
  interface Window {
    refreshCartBadge: Function | null;
    goToCart: Function | null;
  }
}

@Component({
  components: {
    CharmsFilters,
    Composer,
    ProductSelector,
    Gallery,
    Summary
  }
})
export default class Step3 extends Vue {
  charms: Array<Charm> = []

  baseDetail: BaseDetail | null = null
  productDetail: ProductDetail | null = null;
  charmsFilter: CharmTag | null = null
  groupedCharms: any = {}

  selectedCharmsProducts: ProductDetail[] = []
  composerWidth = 0;

  private step3Strings = strings.Step3

  private compositionImage: string | null = null;

  private onWindowResize () {
    const composerElement = document.getElementById('composer')
    if (composerElement) {
      this.composerWidth = composerElement.offsetWidth
    }
  }

  onComposerMounted () {
    this.onWindowResize()
    window.addEventListener('resize', this.onWindowResize)
  }

  private charmsForTag (charms: BaseCharm[], tag: CharmTag) {
    return charms.filter((charm: BaseCharm) => {
      for (const charmTag of charm.tags) {
        if (charmTag.id === tag.id) {
          return true
        }
      }
      return false
    }).sort((charm1: BaseCharm, charm2: BaseCharm) => {
      let charm1Weight = 0
      let charm2Weight = 0

      for (const charmTag of charm1.tags) {
        if (charmTag.id === tag.id) {
          charm1Weight = charmTag.weight
        }
      }

      for (const charmTag of charm2.tags) {
        if (charmTag.id === tag.id) {
          charm2Weight = charmTag.weight
        }
      }
      return charm1Weight - charm2Weight
    })
  }

  async created () {
    this.$store.commit(MutationTypes.CHANGE_LOADING, { loading: true })
    this.baseDetail = await this.$apiService.getBaseDetails(this.baseProductId)

    if (this.baseDetail) {
      for (const product of this.baseDetail.products) {
        if (product.productId === this.baseProductId) {
          this.productDetail = new ProductDetail(product, true, this.baseDetail.idPrestashopMaster)
          break
        }
      }
      for (const tag of this.baseDetail.tags) {
        this.groupedCharms[tag.id] = this.charmsForTag(this.baseDetail.charms, tag)
      }
    }

    setTimeout(() => this.$store.commit(MutationTypes.CHANGE_LOADING, { loading: false }), 500)
  }

  beforeDestroy () {
    window.removeEventListener('resize', this.onWindowResize)
  }

  get inEcommerce (): boolean {
    return window.location.host.includes(process.env.VUE_APP_IN_ECOMMERCE_DOMAIN_SEARCH || '')
  }

  get baseProductId (): number {
    return this.$store.state.baseProductId
  }

  get selectedCharms (): Array<Charm> {
    return this.charms
  }

  get summaryProducts (): ProductDetail[] {
    return this.productDetail ? [this.productDetail, ...this.selectedCharmsProducts] : [...this.selectedCharmsProducts]
  }

  onProductClick (product: ProductDetail) {
    this.updateSelectedCharms({ productToAdd: product })
  }

  updateSelectedCharms (operation: { productToAdd?: ProductDetail; productIndexToRemove?: number }) {
    if (this.baseDetail) {
      if (operation.productToAdd) {
        if (this.baseDetail.hotspots.length === 1) {
          this.selectedCharmsProducts = [operation.productToAdd]
        } else if (this.baseDetail.hotspots.length < this.charms.length + 1) {
          window.VoerroModal.show({
            title: this.step3Strings.charmsLimitTitle,
            body: this.step3Strings.charmsLimitDescription,
            buttons: [
              {
                text: this.step3Strings.charmsLimitOk
              }
            ]
          })
          return
        } else {
          this.selectedCharmsProducts.push(operation.productToAdd)
        }
      }

      if (operation.productIndexToRemove) {
        this.selectedCharmsProducts.splice(operation.productIndexToRemove - 1, 1)
      }

      this.relocateCharms(this.baseDetail.hotspots, this.selectedCharmsProducts)
    }
  }

  onProductRemove (productIndex: number) {
    this.updateSelectedCharms({ productIndexToRemove: productIndex })
  }

  relocateCharms (hotspots: HotSpot[], charmsProducts: ProductDetail[]) {
    const newCharms = []

    const middle = Math.round(hotspots.length / 2)
    let currentIndex = Math.max(middle - Math.round(charmsProducts.length / 2), 0)
    // console.log('middle: ' + middle + ' --- currentIndex: ' + currentIndex)

    for (const charmData of charmsProducts) {
      const hotspot = hotspots[currentIndex]
      if (charmData.image != null) {
        newCharms.push(new Charm(charmData.id, charmData.image, hotspot.x, hotspot.y))
      }
      currentIndex++
    }

    this.charms = newCharms
  }

  get filteredCharms (): BaseCharm[] {
    if (this.baseDetail) {
      if (this.charmsFilter === null) {
        return this.baseDetail?.charms
      } else {
        return this.groupedCharms[this.charmsFilter.id]
      }
    }

    return []
  }

  filterCharms (tag?: CharmTag) {
    if (tag) {
      this.charmsFilter = tag
    } else {
      this.charmsFilter = null
    }
  }

  async onCreatePdf (totalAmount: string) {
    if (this.baseDetail) {
      this.$store.commit(MutationTypes.CHANGE_LOADING, { loading: true })
      const pdfBuilder = new PdfBuilder(this.summaryProducts, totalAmount)
      await this.$apiService.getCompositionPdf(this.baseDetail.productId, this.charms, pdfBuilder.pdfContent)
      this.$store.commit(MutationTypes.CHANGE_LOADING, { loading: false })
    }
  }

  async showModal () {
    if (this.baseDetail) {
      this.$store.commit(MutationTypes.CHANGE_LOADING, { loading: true })
      this.compositionImage = await this.$apiService.getCompositionImage(this.baseDetail.productId, this.charms)
      this.$store.commit(MutationTypes.CHANGE_LOADING, { loading: false })
      window.VoerroModal.show('composition')
    }
  }

  async onAddToCart () {
    this.$store.commit(MutationTypes.CHANGE_LOADING, { loading: true })
    const response = await this.$apiService.addProductsToCart(this.summaryProducts)
    this.$store.commit(MutationTypes.CHANGE_LOADING, { loading: false })

    if (!response || !response.success) {
      window.VoerroModal.show({
        title: this.step3Strings.productsNotAddedToCartTitle,
        body: response?.message || 'Si è verificato un errore',
        buttons: [
          {
            text: this.step3Strings.productsAddedToCartOk
          }
        ]
      })
    } else {
      if (window.refreshCartBadge) {
        window.refreshCartBadge()
      }

      window.VoerroModal.show({
        title: this.step3Strings.productsAddedToCartTitle,
        body: this.step3Strings.productsAddedToCartDescription,
        buttons: [
          {
            text: this.step3Strings.goToCart,
            handler: () => {
              if (window.goToCart) {
                window.goToCart()
              }
            }
          }/*,
          {
            text: this.step3Strings.productsAddedToCartOk
          }*/
        ]
      })
    }
  }
}
